/**
 * Entry route utilities for /, /host, and /join deep links.
 * Marketing site links to /host (auto-create lobby) and /join (player join screen).
 */

import { useEffect, useState } from 'react';
import { getHostCodeFromUrl } from './roomCode';

/** Dispatched after pushState/replaceState so React can re-render for /host and /join */
export const ENTRY_ROUTE_CHANGE_EVENT = 'entryroutechange';

function notifyEntryRouteChange(): void {
  window.dispatchEvent(new Event(ENTRY_ROUTE_CHANGE_EVENT));
}

export const ENTRY_ROUTES = {
  ROOT: '/',
  HOST: '/host',
  JOIN: '/join',
} as const;

export type EntryRoute = (typeof ENTRY_ROUTES)[keyof typeof ENTRY_ROUTES];

/** How long to wait for session restore before falling back to route-specific UI */
export const RESTORE_GIVE_UP_MS = 2000;

function normalizePath(path: string): string {
  const withoutTrailing = path.replace(/\/+$/, '');
  return withoutTrailing || ENTRY_ROUTES.ROOT;
}

export function getCurrentPath(): string {
  return normalizePath(window.location.pathname);
}

export function isRootPath(path: string = getCurrentPath()): boolean {
  return path === ENTRY_ROUTES.ROOT;
}

export function isHostEntryPath(path: string = getCurrentPath()): boolean {
  return path === ENTRY_ROUTES.HOST;
}

/** /host auto-create (not premium ?host= deep link) — always create a new room, never restore session */
export function isImplicitHostCreatePath(path: string = getCurrentPath()): boolean {
  return isHostEntryPath(path) && !getHostCodeFromUrl();
}

export function isJoinEntryPath(path: string = getCurrentPath()): boolean {
  return path === ENTRY_ROUTES.JOIN;
}

export function isPaymentSuccessPath(path: string = getCurrentPath()): boolean {
  return path.includes('/payment/success');
}

type NavigateOptions = {
  replace?: boolean;
  preserveSearch?: boolean;
};

function buildUrl(path: string, options?: NavigateOptions): string {
  const search = options?.preserveSearch ? window.location.search : '';
  return path + search;
}

export function navigateTo(path: string, options?: NavigateOptions): void {
  const url = buildUrl(path, options);
  if (options?.replace) {
    window.history.replaceState({}, '', url);
  } else {
    window.history.pushState({}, '', url);
  }
  notifyEntryRouteChange();
}

export function navigateToRoot(replace = false): void {
  navigateTo(ENTRY_ROUTES.ROOT, { replace });
}

export function navigateToHost(replace = false): void {
  navigateTo(ENTRY_ROUTES.HOST, { replace });
}

export function navigateToJoin(options?: NavigateOptions): void {
  navigateTo(ENTRY_ROUTES.JOIN, options);
}

/** Replace /host or /join with / so refresh does not re-trigger entry actions */
export function clearEntryRouteFromUrl(): void {
  const path = getCurrentPath();
  if (path === ENTRY_ROUTES.HOST || path === ENTRY_ROUTES.JOIN) {
    window.history.replaceState({}, '', ENTRY_ROUTES.ROOT + window.location.search);
    notifyEntryRouteChange();
  }
}

/** Consume /join marketing deep link: show join UI state, then clear path (keeps ?room= etc.) */
export function consumeJoinEntryRoute(): void {
  if (isJoinEntryPath()) {
    clearEntryRouteFromUrl();
  }
}

/** React state synced with pathname (pushState does not re-render by itself) */
export function useEntryPath(): string {
  const [path, setPath] = useState(getCurrentPath);

  useEffect(() => {
    const sync = () => setPath(getCurrentPath());
    window.addEventListener('popstate', sync);
    window.addEventListener(ENTRY_ROUTE_CHANGE_EVENT, sync);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener(ENTRY_ROUTE_CHANGE_EVENT, sync);
    };
  }, []);

  return path;
}

/** True when URL indicates the join screen (path or legacy in-app state) */
export function shouldShowJoinScreen(path: string = getCurrentPath(), showJoinRoomFlag = false): boolean {
  return isJoinEntryPath(path) || showJoinRoomFlag;
}
